/**
 * ProductImageGallery Component - Story 7.3: Create Product Detail Page
 * Image carousel with thumbnails for product detail page
 * Uses embla-carousel-react for smooth image navigation
 */

import React, { useCallback, useEffect, useState } from 'react';
import useEmblaCarousel from 'embla-carousel-react';
import { ChevronLeft, ChevronRight, ImageOff } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import type { ProductImageGalleryProps } from '../types/publicMarketplace.types';

/**
 * ProductImageGallery Component
 * Displays a main image with thumbnail navigation
 *
 * Features:
 * - Main image display (large, centered)
 * - Thumbnail carousel (5 thumbnails visible, swipeable)
 * - Keyboard navigation (arrow keys)
 * - Responsive design (mobile + desktop)
 * - Active thumbnail highlighting
 * - Fallback for missing images
 *
 * @component
 * @example
 * <ProductImageGallery
 *   images={product.images}
 *   productName={product.name}
 * />
 */
export const ProductImageGallery: React.FC<ProductImageGalleryProps> = ({
  images,
  productName,
  className,
}) => {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mainEmblaRef, mainEmblaApi] = useEmblaCarousel({ loop: true });
  const [thumbEmblaRef, thumbEmblaApi] = useEmblaCarousel({
    containScroll: 'keepSnaps',
    dragFree: true,
  });

  // Handle thumbnail click
  const onThumbClick = useCallback(
    (index: number) => {
      if (!mainEmblaApi || !thumbEmblaApi) return;
      mainEmblaApi.scrollTo(index);
    },
    [mainEmblaApi, thumbEmblaApi]
  );

  // Navigate to previous image
  const scrollPrev = useCallback(() => {
    if (mainEmblaApi) mainEmblaApi.scrollPrev();
  }, [mainEmblaApi]);

  // Navigate to next image
  const scrollNext = useCallback(() => {
    if (mainEmblaApi) mainEmblaApi.scrollNext();
  }, [mainEmblaApi]);

  // Update selected index when main carousel changes
  const onSelect = useCallback(() => {
    if (!mainEmblaApi || !thumbEmblaApi) return;
    setSelectedIndex(mainEmblaApi.selectedScrollSnap());
    thumbEmblaApi.scrollTo(mainEmblaApi.selectedScrollSnap());
  }, [mainEmblaApi, thumbEmblaApi, setSelectedIndex]);

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'ArrowLeft') {
        event.preventDefault();
        scrollPrev();
      } else if (event.key === 'ArrowRight') {
        event.preventDefault();
        scrollNext();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [scrollPrev, scrollNext]);

  // Set up carousel event listeners
  useEffect(() => {
    if (!mainEmblaApi) return;
    onSelect();
    mainEmblaApi.on('select', onSelect);
    return () => {
      mainEmblaApi.off('select', onSelect);
    };
  }, [mainEmblaApi, onSelect]);

  // Handle empty images
  if (!images || images.length === 0) {
    return (
      <div
        className={cn(
          'flex flex-col items-center justify-center',
          'aspect-square w-full rounded-lg border-2 border-dashed border-gray-300 bg-gray-50',
          className
        )}
      >
        <ImageOff className="h-16 w-16 text-gray-400 mb-4" />
        <p className="text-sm text-gray-500">No images available</p>
      </div>
    );
  }

  // Sort images to show primary first
  const sortedImages = [...images].sort((a, b) => {
    if (a.isPrimary && !b.isPrimary) return -1;
    if (!a.isPrimary && b.isPrimary) return 1;
    return 0;
  });

  return (
    <div className={cn('w-full space-y-4', className)}>
      {/* Main Image Carousel */}
      <div className="relative w-full">
        <div
          className="overflow-hidden rounded-lg"
          ref={mainEmblaRef}
          role="region"
          aria-label="Product images carousel"
        >
          <div className="flex">
            {sortedImages.map((image, index) => (
              <div
                key={image.id || index}
                className="relative min-w-0 flex-[0_0_100%]"
              >
                <div className="aspect-square w-full bg-gray-100">
                  <img
                    src={image.url}
                    alt={image.altText || `${productName} - Image ${index + 1}`}
                    className="h-full w-full object-cover"
                    loading={index === 0 ? 'eager' : 'lazy'}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Navigation Buttons - Only show if more than 1 image */}
        {sortedImages.length > 1 && (
          <>
            <Button
              variant="secondary"
              size="icon"
              className="absolute left-4 top-1/2 -translate-y-1/2 rounded-full shadow-lg"
              onClick={scrollPrev}
              aria-label="Previous image"
            >
              <ChevronLeft className="h-5 w-5" />
            </Button>
            <Button
              variant="secondary"
              size="icon"
              className="absolute right-4 top-1/2 -translate-y-1/2 rounded-full shadow-lg"
              onClick={scrollNext}
              aria-label="Next image"
            >
              <ChevronRight className="h-5 w-5" />
            </Button>
          </>
        )}

        {/* Image Counter */}
        {sortedImages.length > 1 && (
          <div className="absolute bottom-4 left-1/2 -translate-x-1/2 rounded-full bg-black/60 px-3 py-1 text-sm text-white">
            {selectedIndex + 1} / {sortedImages.length}
          </div>
        )}
      </div>

      {/* Thumbnail Carousel - Only show if more than 1 image */}
      {sortedImages.length > 1 && (
        <div className="w-full" ref={thumbEmblaRef}>
          <div className="flex gap-2">
            {sortedImages.map((image, index) => (
              <button
                key={image.id || index}
                type="button"
                className={cn(
                  'relative min-w-0 flex-[0_0_20%] cursor-pointer overflow-hidden rounded-md border-2 transition-all',
                  'hover:opacity-100 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2',
                  index === selectedIndex
                    ? 'border-primary opacity-100'
                    : 'border-gray-300 opacity-60'
                )}
                onClick={() => onThumbClick(index)}
                aria-label={`View image ${index + 1}`}
                aria-current={index === selectedIndex ? 'true' : 'false'}
              >
                <div className="aspect-square w-full bg-gray-100">
                  <img
                    src={image.url}
                    alt={`Thumbnail ${index + 1}`}
                    className="h-full w-full object-cover"
                    loading="lazy"
                  />
                </div>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Accessibility: Keyboard navigation hint */}
      <div className="sr-only" role="status" aria-live="polite">
        Image {selectedIndex + 1} of {sortedImages.length}. Use arrow keys to navigate.
      </div>
    </div>
  );
};

export default ProductImageGallery;
